import { Component, Inject, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

export interface PhotoViewerData {
  url: string;
}

@Component({
  selector: 'app-photo-viewer-dialog',
  template: `
    <div class="pv-toolbar">
      <button class="pv-btn" (click)="zoomOut()" title="Zoom out">−</button>
      <span class="pv-zoom-pct">{{ zoomPercent }}%</span>
      <button class="pv-btn" (click)="zoomIn()" title="Zoom in">+</button>
      <button class="pv-btn" (click)="reset()" title="Reset view">↺</button>
      <span class="pv-spacer"></span>
      <button class="pv-btn pv-newtab-btn" (click)="openInNewTab()" title="Open in new tab">⤢</button>
      <button class="pv-btn pv-close-btn" (click)="close()" title="Close">✕</button>
    </div>

    <div class="pv-canvas"
         (wheel)="onWheel($event)"
         (mousedown)="onMouseDown($event)"
         (mousemove)="onMouseMove($event)"
         (mouseup)="onMouseUp()"
         (mouseleave)="onMouseUp()"
         (touchstart)="onTouchStart($event)"
         (touchmove)="onTouchMove($event)"
         (touchend)="onTouchEnd()">
      <img [src]="data.url"
           [style.transform]="imgTransform"
           [style.cursor]="dragging ? 'grabbing' : 'grab'"
           (dblclick)="onDblClick()"
           draggable="false">
    </div>

    <div class="pv-hint">Scroll to zoom · Drag to pan · Double-click to reset</div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #1a1a1a;
      overflow: hidden;
    }

    .pv-toolbar {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background: #242424;
      border-bottom: 1px solid #333;
      flex-shrink: 0;
    }

    .pv-btn {
      background: #444;
      color: #fff;
      border: none;
      border-radius: 4px;
      min-width: 32px;
      height: 32px;
      padding: 0 8px;
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      transition: background 0.15s;
      flex-shrink: 0;
    }
    .pv-btn:hover { background: #5a5a5a; }

    .pv-close-btn { background: #8b2a2a; }
    .pv-close-btn:hover { background: #b03535; }

    .pv-newtab-btn { font-size: 18px; }

    .pv-zoom-pct {
      color: #999;
      font-size: 13px;
      min-width: 52px;
      text-align: center;
      font-family: monospace;
    }

    .pv-spacer { flex: 1; }

    .pv-canvas {
      flex: 1;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }

    .pv-canvas img {
      max-width: 92%;
      max-height: 100%;
      object-fit: contain;
      user-select: none;
      -webkit-user-drag: none;
      transform-origin: center center;
      will-change: transform;
    }

    .pv-hint {
      text-align: center;
      font-size: 11px;
      color: #484848;
      padding: 5px;
      flex-shrink: 0;
    }

    @media (max-width: 600px) {
      .pv-toolbar {
        gap: 4px;
        padding: 6px 6px;
      }
      .pv-btn {
        min-width: 40px;
        height: 40px;
        padding: 0 6px;
        font-size: 18px;
      }
      .pv-newtab-btn { font-size: 20px; }
      .pv-zoom-pct {
        font-size: 12px;
        min-width: 38px;
      }
      .pv-canvas img {
        max-width: 100%;
      }
      .pv-hint {
        font-size: 10px;
        padding: 3px;
      }
    }

    @media (max-width: 380px) {
      .pv-hint { display: none; }
    }
  `]
})
export class PhotoViewerDialogComponent {
  scale = 1;
  tx = 0;
  ty = 0;
  dragging = false;

  private lastMouseX = 0;
  private lastMouseY = 0;
  private lastTouchX = 0;
  private lastTouchY = 0;

  get zoomPercent() { return Math.round(this.scale * 100); }

  get imgTransform(): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(
      `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
    );
  }

  constructor(
    public dialogRef: MatDialogRef<PhotoViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PhotoViewerData,
    private sanitizer: DomSanitizer
  ) {}

  close() { this.dialogRef.close(); }
  reset() { this.scale = 1; this.tx = 0; this.ty = 0; }
  zoomIn() { this.scale = Math.min(this.scale * 1.25, 12); }
  zoomOut() { this.scale = Math.max(this.scale / 1.25, 0.1); }
  onDblClick() { this.reset(); }

  openInNewTab() {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.body.style.cssText = 'margin:0;background:#111;';
    const img = win.document.createElement('img');
    img.src = this.data.url;
    img.style.cssText = 'display:block;max-width:100%;margin:0 auto;';
    win.document.body.appendChild(img);
  }

  onWheel(e: WheelEvent) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    this.scale = Math.min(Math.max(this.scale * factor, 0.1), 12);
  }

  onMouseDown(e: MouseEvent) {
    this.dragging = true;
    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
    e.preventDefault();
  }

  onMouseMove(e: MouseEvent) {
    if (!this.dragging) return;
    this.tx += e.clientX - this.lastMouseX;
    this.ty += e.clientY - this.lastMouseY;
    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
  }

  onMouseUp() { this.dragging = false; }

  onTouchStart(e: TouchEvent) {
    if (e.touches.length === 1) {
      this.dragging = true;
      this.lastTouchX = e.touches[0].clientX;
      this.lastTouchY = e.touches[0].clientY;
    }
  }

  onTouchMove(e: TouchEvent) {
    e.preventDefault();
    if (e.touches.length === 1 && this.dragging) {
      this.tx += e.touches[0].clientX - this.lastTouchX;
      this.ty += e.touches[0].clientY - this.lastTouchY;
      this.lastTouchX = e.touches[0].clientX;
      this.lastTouchY = e.touches[0].clientY;
    }
  }

  onTouchEnd() { this.dragging = false; }

  @HostListener('document:keydown.escape')
  onEsc() { this.close(); }
}
